import { css } from '@emotion/react';
import { Trans } from 'i18n';
import React from 'react';
import { matchPath, useLocation, NavLink } from 'react-router-dom';

import { prependMarketToPath } from '../../utils';
import { Content } from '../Content';
import { SasIcon } from '../SasIcon';
import { SasLogo } from '../SasLogo';
import { theme } from '../theme';

import { getUserProfileFromSessionStorage, ProfileLogo, ProfileLogoProps } from './ProfileLogo';
import * as styles from './styles';

export type NavLink = {
  label: string;
  path: string;
};

type Props = {
  navLinks: NavLink[];
  handleClose?: () => void;
  profileLogoData?: ProfileLogoProps;
  disableNavigationInLastTab?: boolean;
};

const onClose = () => {
  window.location.href = prependMarketToPath('/');
};

const headerStyles = css`
  background-color: #000099;
  color: ${theme.colors.white};
  display: flex;
  align-items: stretch;
  z-index: 2;
  position: relative;
  height: 4rem;

  @media (min-width: ${theme.breakpoints.laptop}) {
    height: 4.675rem;
  }
`;

export function Navigation({
  navLinks,
  profileLogoData,
  disableNavigationInLastTab = true,
  handleClose = onClose,
}: Props) {
  const location = useLocation();

  let selectedIndex = navLinks.findIndex(navLink => matchPath(navLink.path, location.pathname));
  selectedIndex = selectedIndex === -1 ? 0 : selectedIndex;

  const isLastPage = selectedIndex === navLinks.length - 1;
  const isFirstPage = selectedIndex === 0;

  const showBackIcon = !isFirstPage && !isLastPage;

  const navigateToStartpage = () => {
    window.location.href = prependMarketToPath('/');
  };

  const navItems = navLinks.map((navLink, index) => {
    const { path, label } = navLink;
    const labelWithNumber = `${index + 1} ${label}`;
    const selected = selectedIndex === index;

    const isEnabled = selectedIndex > index && (!isLastPage || !disableNavigationInLastTab);

    return (
      <React.Fragment key={path}>
        {isEnabled ? (
          <NavLink to={path} css={styles.linkItem(selected, true)}>
            {labelWithNumber}
          </NavLink>
        ) : (
          <div css={styles.linkItem(selected, false)}>{labelWithNumber}</div>
        )}
      </React.Fragment>
    );
  });

  let profileLogoProps = profileLogoData;
  if (!profileLogoProps) {
    profileLogoProps = getUserProfileFromSessionStorage();
  }

  return (
    <header css={headerStyles}>
      <Content>
        <div css={styles.wrapper}>
          <div css={styles.logo}>
            <div css={styles.sasLogoLink} onClick={navigateToStartpage}>
              <SasLogo />
            </div>
          </div>

          <div css={styles.backLinkWrapper}>
            {showBackIcon ? (
              <NavLink to={navLinks[selectedIndex - 1].path} css={styles.backLink}>
                <SasIcon name="in--arrow-left" color={theme.colors.white} />
              </NavLink>
            ) : (
              <SasLogo />
            )}
          </div>

          <div css={styles.mobileTitle}>
            <div>
              <Trans i18nKey="nav.steps">
                {{ current: selectedIndex + 1 }}{' '}
                <span css={styles.mobileTitleSmall}>of {{ total: navLinks.length }}</span>
              </Trans>
            </div>
            <div>{navLinks[selectedIndex].label}</div>
          </div>

          <div css={styles.links}>{navItems}</div>

          <div style={{ display: 'flex', alignItems: 'center' }}>
            {profileLogoProps ? (
              <ProfileLogo {...profileLogoProps} />
            ) : (
              <div css={styles.closeIcon} onClick={handleClose}>
                <SasIcon name="in--close" color={theme.colors.white} />
              </div>
            )}
          </div>
        </div>
      </Content>
    </header>
  );
}
